import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatChangeValue, prettyJson, toChangeRows } from '../lib/changes';
import { useCopyToClipboard } from '@shared/lib/use-copy-to-clipboard';
import { CopyFeedbackIcon } from '@shared/ui';

export function AuditChangesView({ changes }: { changes: unknown }) {
  const { t } = useTranslation('cabinet');
  const rows = toChangeRows(changes);
  if (changes == null) return null;

  if (!rows) {
    return <AuditJsonView title={t('auditLog.detail.changes')} value={changes} />;
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-foreground">{t('auditLog.detail.changes')}</h3>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-left text-sm">
          <thead className="bg-muted/60 text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">{t('auditLog.detail.field')}</th>
              <th className="px-3 py-2 font-medium">{t('auditLog.detail.from')}</th>
              <th className="px-3 py-2 font-medium">{t('auditLog.detail.to')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.field} className="border-t border-border align-top">
                <td className="px-3 py-2 font-mono text-xs">{row.field}</td>
                <td className="whitespace-pre-wrap break-all px-3 py-2 text-muted-foreground">
                  {formatChangeValue(row.from)}
                </td>
                <td className="whitespace-pre-wrap break-all px-3 py-2">
                  {formatChangeValue(row.to)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AuditJsonView({ title, value }: { title: string; value: unknown }) {
  const { t } = useTranslation('cabinet');
  const { copy, isCopied } = useCopyToClipboard();
  const [open, setOpen] = useState(false);
  const text = prettyJson(value);
  const long = text.length > 400;
  const copyKey = `audit-json-${title}`;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium text-foreground">{title}</h3>
        <button
          type="button"
          className="inline-flex min-h-tap items-center gap-1 text-xs text-primary sm:min-h-0"
          onClick={() => void copy(text, t('auditLog.copied'), copyKey)}
        >
          <CopyFeedbackIcon copied={isCopied(copyKey)} className="h-3.5 w-3.5" />
          {t('auditLog.copy')}
        </button>
      </div>
      {long && !open ? (
        <button
          type="button"
          className="text-xs text-primary hover:underline"
          onClick={() => setOpen(true)}
        >
          {t('auditLog.detail.expand')}
        </button>
      ) : (
        <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-muted/50 p-3 font-mono text-xs text-foreground">
          {text}
        </pre>
      )}
    </div>
  );
}
